import { useMemo } from 'react'

import { toast } from 'react-toastify'
import { useSwitchChain } from 'wagmi'

import * as Sentry from '@sentry/react'

import { ChainId, WRAPPED_GAS_TOKENS } from '@src/configs/chainInfo'
import { LiquiditySource, ResultCode, StepNames } from '@src/enums'
import { useExchangeTokenEvm } from '@src/hooks/useExchangeToken/useExchangeTokenEvm'
import { useExchangeTokenSolana } from '@src/hooks/useExchangeToken/useExchangeTokenSolana'
import { useExchangeTokenTron } from '@src/hooks/useExchangeToken/useExchangeTokenTron'
import { useJupiterSolanaHandler } from '@src/hooks/useExchangeToken/useJupiterSolanaHandler'
import { useWalletConnections } from '@src/hooks/useWalletConnections'
import { useExchangeDataContext } from '@src/pages/Exchange/context/ExchangeDataContext'
import { useSwapContract } from '@src/pages/Exchange/hooks/useSwapContract'
import { analyticsService } from '@src/services/analytics'
import { ChainHandler, IExchangeData, UpdateData } from '@src/types/exchange'
import { IQuoteData } from '@src/types/order'
import { getChecksumAddress } from '@src/utils/address'
import { isFeatureEnabled } from '@src/utils/environment'
import { isUnWrapOnly, isWrapOnly } from '@src/utils/exchangeUtils'
import { getAllowance } from '@src/utils/getAllowance'
import { getBlockExplorerUrlFromChainId } from '@src/utils/getBlockExplorerUrl'
import { createContextLogger } from '@src/utils/logger'
import { notifySlackOfError } from '@src/utils/slackNotifier'
import { mapTokenAddress } from '@src/utils/tokenAddressMapping'
import { useApplicationStore } from '@src/zustand_stores/application.store'
import { useConfigStore } from '@src/zustand_stores/config.store'
import { DEFAULT_STEPS, useHistoryStore } from '@src/zustand_stores/history.store'
import { useInvalidateBalances } from '@src/zustand_stores/tokens.store'

interface ExchangeTokenReturn {
    exchange: (referrer?: string, quote?: IQuoteData) => Promise<void>
}

const logger = createContextLogger('useExchangeToken')

export const useExchangeToken = (
    exchangeId: string,
    liquiditySource: LiquiditySource,
    destinationAddress?: string,
    // eslint-disable-next-line
    onSwapSuccess: () => void = () => {}
): ExchangeTokenReturn => {
    // Get available networks from config store
    const availableNetworks = useConfigStore(state => state.availableNetworks)

    // Zustand hooks
    const setWaitingForConnectedWalletInteraction = useApplicationStore(
        state => state.setWaitingForConnectedWalletInteraction
    )
    const getOrder = useHistoryStore(state => state.getOrder)

    const updateHistory = useHistoryStore(state => state.updateHistory)
    const { currentStep } = getOrder(exchangeId)

    // Add the invalidateBalances function
    const invalidateBalances = useInvalidateBalances()

    /**
     * * * * * * * * * * * * * * * * * * * * * NEW EXCHANGE DATA CONTEXT * * * * * * * * * * * * * * *
     */
    const { sellData, buyData } = useExchangeDataContext()
    /**
     * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
     */

    // Chain switching
    const { switchChainAsync } = useSwitchChain()

    // Wallet connections
    const { tronWalletAddress, solanaWalletAddress, EVMChainId, evmWalletAddress } = useWalletConnections()

    // Contract setup
    const swapContract = useSwapContract(sellData, liquiditySource)

    // Initialize chain handlers
    const evmHandler = useExchangeTokenEvm(swapContract)
    const tronHandler = useExchangeTokenTron(swapContract)
    const solanaHandler = useExchangeTokenSolana()
    const jupiterSolanaHandler = useJupiterSolanaHandler()

    // Get appropriate chain handler based on chainId and liquidity source
    const getChainHandler = useMemo(() => {
        // For Jupiter swaps on Solana, use the Jupiter handler
        if (liquiditySource === LiquiditySource.jupiter && sellData.chainId === ChainId.Solana) {
            return () => jupiterSolanaHandler
        }

        // For other chains/sources, use the standard handlers
        const handlers = {
            [ChainId.Solana]: solanaHandler,
            [ChainId.Tron]: tronHandler,
        } as Record<number, ChainHandler>
        return (chainId: number) => handlers[chainId] || evmHandler
    }, [solanaHandler, tronHandler, evmHandler, jupiterSolanaHandler, liquiditySource, sellData.chainId])

    // Handle network switching for EVM chains
    const handleNetworkSwitch = async (targetChainId: number) => {
        if (targetChainId !== ChainId.Solana && targetChainId !== ChainId.Tron && targetChainId !== EVMChainId) {
            const updateData = {
                result: {
                    status: ResultCode.pending,
                    message: 'Time to switch network.',
                    transactionETA: 0,
                },
            }

            try {
                // Log detailed chain switch attempt for debugging
                logger.info('Attempting to switch network', {
                    from: EVMChainId,
                    to: targetChainId,
                    isTestnetEnvironment: isFeatureEnabled('USE_TESTNETS_ONLY'),
                    isTestnetEnabled: isFeatureEnabled('ENABLE_TESTNETS'),
                })

                updateHistory(exchangeId, updateData)
                await switchChainAsync({ chainId: targetChainId })

                logger.info('Network switch successful', { currentChainId: targetChainId })
            } catch (error) {
                // Get the chain name for a user-friendly error message
                const targetChainName = availableNetworks[targetChainId]?.name || `Chain ID ${targetChainId}`

                logger.error('Network switch failed', {
                    from: EVMChainId,
                    to: targetChainId,
                    isTestnetEnvironment: isFeatureEnabled('USE_TESTNETS_ONLY'),
                    isTestnetEnabled: isFeatureEnabled('ENABLE_TESTNETS'),
                    originalError: error,
                })

                // Show a user-friendly error toast
                toast.error(`Please manually switch to ${targetChainName} network in your wallet to continue.`, {
                    position: 'bottom-right',
                    autoClose: 7000,
                })

                throw new Error(`Please switch to ${targetChainName} network in your wallet to continue.`) // Throw a more user-friendly error
            }
        }
    }

    // Get wallet address based on chain
    const getWalletAddress = (chainId: number): string => {
        switch (chainId) {
            case ChainId.Solana:
                return solanaWalletAddress || ''
            case ChainId.Tron:
                return tronWalletAddress || ''
            default:
                return evmWalletAddress || ''
        }
    }

    const validateInputs = (sellData: IExchangeData, buyData: IExchangeData, quote?: IQuoteData) => {
        logger.debug('Validating inputs:', {
            sellToken: {
                address: sellData.token?.address,
                symbol: sellData.token?.symbol,
            },
            buyToken: {
                address: buyData.token?.address,
                symbol: buyData.token?.symbol,
            },
            quote: quote
                ? {
                      srcAsset: quote?.src_asset_address,
                      dstAsset: quote?.dst_asset_address,
                  }
                : 'No quote',
        })

        if (!sellData.value || !sellData.token || !buyData.token) {
            logger.error('Missing required data:', {
                'sellData.value': sellData.value,
                'sellData.token': sellData.token,
                'buyData.token': buyData.token,
            })
            throw Error('validateInputs error: Invalid data')
        }

        if (quote) {
            // Map both quote and token addresses
            const mappedSellTokenAddress = mapTokenAddress(sellData.token.address, true, sellData.chainId)
            const mappedQuoteSrcAddress = mapTokenAddress(quote?.src_asset_address, true, sellData.chainId)
            const mappedBuyTokenAddress = mapTokenAddress(buyData.token.address, true, buyData.chainId)
            const mappedQuoteDstAddress = mapTokenAddress(quote?.dst_asset_address, true, buyData.chainId)

            // Convert addresses to checksum format for comparison
            const sellTokenMismatch =
                getChecksumAddress(mappedQuoteSrcAddress) !== getChecksumAddress(mappedSellTokenAddress)
            const buyTokenMismatch =
                getChecksumAddress(mappedQuoteDstAddress) !== getChecksumAddress(mappedBuyTokenAddress)

            logger.debug('Token address comparison:', {
                sell: {
                    quoteAddress: getChecksumAddress(mappedQuoteSrcAddress),
                    tokenAddress: getChecksumAddress(mappedSellTokenAddress),
                    matches: !sellTokenMismatch,
                },
                buy: {
                    quoteAddress: getChecksumAddress(mappedQuoteDstAddress),
                    tokenAddress: getChecksumAddress(mappedBuyTokenAddress),
                    matches: !buyTokenMismatch,
                },
            })

            if (sellTokenMismatch || buyTokenMismatch) {
                logger.debug('Token address mismatch:', {
                    sell: {
                        expected: getChecksumAddress(mappedSellTokenAddress),
                        got: getChecksumAddress(mappedQuoteSrcAddress),
                        symbol: sellData.token.symbol,
                    },
                    buy: {
                        expected: getChecksumAddress(mappedBuyTokenAddress),
                        got: getChecksumAddress(mappedQuoteDstAddress),
                        symbol: buyData.token.symbol,
                    },
                })
                throw Error('validateInputs error: Invalid token address')
            }
        }

        if (liquiditySource === LiquiditySource.unavailable) {
            // Track liquidity unavailable error
            analyticsService.trackEvent('liquidity_unavailable_error', {
                sell_token: sellData.token.symbol,
                buy_token: buyData.token.symbol,
                sell_amount: sellData.value.toString(),
                timestamp: new Date().toISOString(),
            })

            toast.error('Error: No liquidity available or quote expired.', {
                position: 'bottom-right',
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            })
            return
        }
    }

    const isRateLimited = (error: any): boolean => {
        try {
            console.log('Checking rate limit on error:', error)

            // Direct check for our specific rate limit error format from useExchangeTokenEvm
            if (error instanceof Error && error.message.startsWith('Rate limited. Please try again in')) {
                return true
            }

            // Original error has message "Cannot send order data" but comes from a 429
            if (error instanceof Error && error.message === 'Cannot send order data') {
                // Since we know from the logs this is often a 429 error in testnet
                return true
            }

            // Direct status check
            if (error?.status === 429) return true

            // Response status check (axios style)
            if (error?.response?.status === 429) return true

            // Message content check - make sure message exists and is a string
            const message = typeof error?.message === 'string' ? error.message.toLowerCase() : ''
            if (
                message &&
                (message.includes('429') || message.includes('rate limit') || message.includes('too many requests'))
            )
                return true

            // Check detail message which might contain rate limit info
            const detailMessage =
                typeof error?.response?.data?.detail === 'string'
                    ? error.response.data.detail.toLowerCase()
                    : typeof error?.data?.detail === 'string'
                      ? error.data.detail.toLowerCase()
                      : ''

            if (detailMessage && (detailMessage.includes('too many requests') || detailMessage.includes('rate limit')))
                return true

            // Sometimes the error might be wrapped in other objects
            if (error?.data?.status === 429) return true

            return false
        } catch (e) {
            console.error('Error checking rate limit:', e)
            // If anything fails, assume it's not rate limited
            return false
        }
    }

    const getWaitTime = (error: any): number => {
        try {
            // Default wait time
            const defaultTime = 30

            // Check our custom error format first
            if (
                error instanceof Error &&
                error.message &&
                error.message.startsWith('Rate limited. Please try again in')
            ) {
                const match = error.message.match(/Please try again in (\d+) seconds/)
                if (match && match[1]) {
                    const seconds = parseInt(match[1])
                    return isNaN(seconds) ? defaultTime : seconds
                }
            }

            // Safely extract detail message
            const detail = error?.data?.detail || error?.response?.data?.detail

            if (!detail || typeof detail !== 'string') {
                return defaultTime
            }

            // Safely extract wait time with regex
            const waitMatch = detail.match(/Please wait (\d+\.?\d*) seconds/)
            if (waitMatch && waitMatch[1]) {
                const parsedTime = parseFloat(waitMatch[1])
                return isNaN(parsedTime) ? defaultTime : Math.ceil(parsedTime)
            }

            return defaultTime
        } catch {
            // If anything fails, return default wait time
            return 30
        }
    }

    const handleRateLimitedError = (waitTime: number) => {
        console.log('handleRateLimitedError called with waitTime:', waitTime)
        try {
            // Ensure waitTime is valid
            const safeWaitTime = waitTime && waitTime > 0 ? waitTime : 30
            toast.error(`Rate limited. Please try again in ${safeWaitTime} seconds.`)
        } catch {
            // Absolute fallback
            toast.error('Rate limited. Please try again later.')
        }
    }

    const exchange = async (referrer?: string, quote?: IQuoteData): Promise<void> => {
        logger.info('exchange called:', { sellData, buyData, quote })

        logger.info(
            `currentStep=${currentStep} isWrapOnly=${isWrapOnly(sellData, buyData)} isUnWrapOnly=${isUnWrapOnly(sellData, buyData)}`
        )

        try {
            // Initialize common data
            const updateData: UpdateData = {
                exchangeId,
                steps: DEFAULT_STEPS,
                result: {
                    status: ResultCode.pending,
                    message: 'Processing. Please be patient.',
                    transactionETA: 0,
                },
            }

            // Handle network switching if needed
            await handleNetworkSwitch(sellData.chainId)

            // Validate inputs unless it's a wrap/unwrap operation
            if (!isWrapOnly(sellData, buyData) && !isUnWrapOnly(sellData, buyData)) {
                validateInputs(sellData, buyData, quote)
            }

            const chainHandler = getChainHandler(sellData.chainId)
            const walletAddress = getWalletAddress(sellData.chainId)

            if (!walletAddress) {
                throw new Error('No wallet connected')
            }

            logger.info(`[exchange] currentStep=${currentStep} walletAddress=${walletAddress}`)

            // HANDLE WRAPPING FLOW
            if ((currentStep === StepNames.DEPOSIT_WETH || isWrapOnly(sellData, buyData)) && chainHandler.handleWrap) {
                const hash = await chainHandler.handleWrap(sellData.value, updateData, sellData)

                logger.info(`[exchange] Wrap tx hash=${hash}`)

                // Handle case where hash is empty (user rejected transaction)
                if (!hash) {
                    updateHistory(exchangeId, {
                        currentStep: currentStep || StepNames.UNSTARTED,
                        result: {
                            status: ResultCode.pending,
                            message: 'Transaction was rejected. Please try again.',
                        },
                    })
                    setWaitingForConnectedWalletInteraction(false)
                    return
                }

                // If this is a wrap-only operation, complete the flow here
                if (isWrapOnly(sellData, buyData)) {
                    updateHistory(exchangeId, {
                        ...updateData,
                        currentStep: StepNames.MATCH_EXECUTED,
                        result: {
                            ...updateData.result,
                            status: ResultCode.success,
                            sourceTxHash: `${getBlockExplorerUrlFromChainId(sellData.chainId)}/${hash}`,
                        },
                    })

                    onSwapSuccess()
                    invalidateBalances() // Refresh balances after wrap
                    setWaitingForConnectedWalletInteraction(false)
                    return
                }

                // After wrapping, check if approval is needed and auto-advance through the flow
                try {
                    // Get the wrapped token address for this chain
                    const wrappedTokenAddress = WRAPPED_GAS_TOKENS[sellData.chainId].address
                    logger.info(`[exchange] Checking allowance after wrapping for ${wrappedTokenAddress}`)

                    // Check if we need to approve the wrapped token
                    const allowance = await getAllowance(
                        +sellData.chainId,
                        wrappedTokenAddress,
                        walletAddress,
                        swapContract.address
                    )

                    const needsApproval = BigInt(allowance) < BigInt(sellData.value)
                    logger.info(`[exchange] Allowance check after wrapping:`, {
                        allowance,
                        sellValue: sellData.value,
                        needsApproval,
                    })

                    // Handle approval if needed
                    if (needsApproval) {
                        // Update UI to show approval step
                        updateHistory(exchangeId, {
                            currentStep: StepNames.APPROVE_TOKENS,
                            result: {
                                status: ResultCode.pending,
                                message: 'Approving tokens...',
                            },
                        })

                        // Auto-proceed to approval
                        logger.info(`[exchange] Auto-proceeding to approval step after wrap`)

                        // Explicitly update to APPROVE_TOKENS_PENDING before calling handleApproval
                        updateHistory(exchangeId, {
                            currentStep: StepNames.APPROVE_TOKENS_PENDING,
                            result: {
                                status: ResultCode.pending,
                                message: 'Approving tokens...',
                            },
                        })

                        const approvalHash = await chainHandler.handleApproval(updateData, sellData, walletAddress)

                        if (!approvalHash) {
                            updateHistory(exchangeId, {
                                currentStep: StepNames.APPROVE_TOKENS,
                                result: {
                                    status: ResultCode.pending,
                                    message: 'Transaction was rejected. Please try again.',
                                },
                            })
                            setWaitingForConnectedWalletInteraction(false)
                            return
                        }

                        // Refresh allowances after approval
                        invalidateBalances()
                        logger.info(`[exchange] Auto-proceeding to swap after approval`)
                    }

                    // Auto-proceed to swap (whether approval was needed or not)
                    // Explicitly update to CONFIRM_SWAP before proceeding to swap
                    updateHistory(exchangeId, {
                        currentStep: StepNames.CONFIRM_SWAP,
                        result: {
                            status: ResultCode.pending,
                            message: 'Preparing to swap tokens...',
                        },
                    })

                    // Short delay to ensure UI updates are visible
                    await new Promise(resolve => setTimeout(resolve, 500))

                    // Update to CONFIRM_SWAP_PENDING before executing the swap
                    updateHistory(exchangeId, {
                        currentStep: StepNames.CONFIRM_SWAP_PENDING,
                        result: {
                            status: ResultCode.pending,
                            message: 'Swapping tokens...',
                        },
                    })

                    const executeSwapData = {
                        sellData,
                        buyData,
                        quote,
                        walletAddress,
                        destinationAddress,
                        liquiditySource,
                        onSuccess: () => {
                            onSwapSuccess()
                            invalidateBalances()
                        },
                        exchangeId,
                        chainId: sellData.chainId,
                        // For Jupiter swaps, pass the Jupiter quote directly
                        ...(liquiditySource === LiquiditySource.jupiter && {
                            jupiterQuote: quote?.jupiter_quote,
                        }),
                    }

                    logger.info(`[exchange] Proceeding to executeSwap with data=`, executeSwapData)
                    const swapHash = await chainHandler.executeSwap(executeSwapData, referrer)

                    if (!swapHash) {
                        updateHistory(exchangeId, {
                            currentStep: StepNames.CONFIRM_SWAP,
                            result: {
                                status: ResultCode.pending,
                                message: 'Transaction was rejected. Please try again.',
                            },
                        })
                        setWaitingForConnectedWalletInteraction(false)
                    }

                    // Exit the function since we've completed the entire flow
                    return
                } catch (error) {
                    logger.error(`[exchange] Error in auto-flow after wrap:`, error)
                    // Provide a more specific error message based on the attempted operation
                    updateHistory(exchangeId, {
                        currentStep: StepNames.DEPOSIT_WETH, // Keep at wrap step so user can retry
                        result: {
                            status: ResultCode.failure,
                            message: 'Error after wrapping. Please try again.',
                        },
                    })
                    setWaitingForConnectedWalletInteraction(false)
                }
            }

            // HANDLE UNWRAPPING FLOW
            if (
                (currentStep === StepNames.UNWRAP_WETH || isUnWrapOnly(sellData, buyData)) &&
                chainHandler.handleUnwrap
            ) {
                const hash = await chainHandler.handleUnwrap(sellData.value, updateData, sellData)

                // Handle case where hash is empty (user rejected transaction)
                if (!hash) {
                    updateHistory(exchangeId, {
                        currentStep: currentStep || StepNames.UNSTARTED,
                        result: {
                            status: ResultCode.pending,
                            message: 'Transaction was rejected. Please try again.',
                        },
                    })
                    setWaitingForConnectedWalletInteraction(false)
                    return
                }

                // If this is an unwrap-only operation, complete the flow here
                if (isUnWrapOnly(sellData, buyData)) {
                    updateHistory(exchangeId, {
                        ...updateData,
                        currentStep: StepNames.MATCH_EXECUTED,
                        result: {
                            ...updateData.result,
                            status: ResultCode.success,
                            sourceTxHash: `${getBlockExplorerUrlFromChainId(sellData.chainId)}/${hash}`,
                        },
                    })

                    onSwapSuccess()
                    invalidateBalances() // Refresh balances after unwrap
                    setWaitingForConnectedWalletInteraction(false)
                    return
                }
            }

            // HANDLE APPROVAL AND SWAP FLOWS
            try {
                // Handle approval if needed
                if (currentStep === StepNames.APPROVE_TOKENS) {
                    logger.info(`[exchange] Calling handleApproval...`)
                    const hash = await chainHandler.handleApproval(updateData, sellData, walletAddress)
                    if (!hash) {
                        updateHistory(exchangeId, {
                            currentStep: currentStep || StepNames.UNSTARTED,
                            result: {
                                status: ResultCode.pending,
                                message: 'Transaction was rejected. Please try again.',
                            },
                        })
                        setWaitingForConnectedWalletInteraction(false)
                        return
                    }

                    // Refresh allowances after approval
                    invalidateBalances()

                    // Auto-proceed to swap after approval
                    logger.info(`[exchange] Auto-proceeding to swap after approval`)

                    // Execute the swap
                    const executeSwapData = {
                        sellData,
                        buyData,
                        quote,
                        walletAddress,
                        destinationAddress,
                        liquiditySource,
                        onSuccess: () => {
                            onSwapSuccess()
                            invalidateBalances()
                        },
                        exchangeId,
                        chainId: sellData.chainId,
                        // For Jupiter swaps, pass the Jupiter quote directly
                        ...(liquiditySource === LiquiditySource.jupiter && {
                            jupiterQuote: quote?.jupiter_quote,
                        }),
                    }

                    logger.info(`[exchange] Proceeding to executeSwap with data=`, executeSwapData)
                    const swapHash = await chainHandler.executeSwap(executeSwapData, referrer)

                    if (!swapHash) {
                        updateHistory(exchangeId, {
                            currentStep: StepNames.CONFIRM_SWAP,
                            result: {
                                status: ResultCode.pending,
                                message: 'Transaction was rejected. Please try again.',
                            },
                        })
                    }

                    // Return early since we've handled the whole flow
                    setWaitingForConnectedWalletInteraction(false)
                    return
                }

                // If we're at CONFIRM_SWAP (not handled by auto-flow), execute the swap
                if (currentStep === StepNames.CONFIRM_SWAP) {
                    // Execute the swap
                    const executeSwapData = {
                        sellData,
                        buyData,
                        quote,
                        walletAddress,
                        destinationAddress,
                        liquiditySource,
                        onSuccess: () => {
                            onSwapSuccess()
                            invalidateBalances()
                        },
                        exchangeId,
                        chainId: sellData.chainId,
                        // For Jupiter swaps, pass the Jupiter quote directly
                        ...(liquiditySource === LiquiditySource.jupiter && {
                            jupiterQuote: quote?.jupiter_quote,
                        }),
                    }

                    logger.info(`[exchange] Proceeding to executeSwap with data=`, executeSwapData)
                    const swapHash = await chainHandler.executeSwap(executeSwapData, referrer)

                    if (!swapHash) {
                        updateHistory(exchangeId, {
                            currentStep: StepNames.CONFIRM_SWAP,
                            result: {
                                status: ResultCode.pending,
                                message: 'Transaction was rejected. Please try again.',
                            },
                        })
                    }

                    // Return early since we've handled the whole flow
                    setWaitingForConnectedWalletInteraction(false)
                    return
                }
            } catch (error) {
                const isUserRejection =
                    error instanceof Error &&
                    (error.message.includes('User rejected') ||
                        error.message.includes('User denied') ||
                        error.message.toLowerCase().includes('rejected by user'))

                if (isUserRejection) {
                    logger.info('User rejected the transaction')
                    updateHistory(exchangeId, {
                        currentStep: currentStep || StepNames.UNSTARTED,
                        result: {
                            status: ResultCode.pending,
                            message: 'Transaction was rejected. Please try again.',
                        },
                    })
                    setWaitingForConnectedWalletInteraction(false)
                    return
                }

                logger.error('Exchange error:outerTryCatch', error)

                // Determine which step failed based on currentStep for better error messages
                let errorMessage = 'Transaction failed. Please try again.'
                const failedStep = currentStep || StepNames.UNSTARTED

                switch (currentStep) {
                    case StepNames.DEPOSIT_WETH:
                        errorMessage = 'Wrapping failed. Please try again.'
                        break
                    case StepNames.APPROVE_TOKENS:
                        errorMessage = 'Approval failed. Please try again.'
                        break
                    case StepNames.CONFIRM_SWAP:
                        errorMessage = 'Swap failed. Please try again.'
                        break
                }

                // Log error details to Sentry
                Sentry.captureException(error, {
                    tags: {
                        currentStep,
                        walletAddress,
                        destinationAddress,
                        liquiditySource,
                        sellToken: sellData.token?.symbol,
                        buyToken: buyData.token?.symbol,
                        sellChainId: sellData.chainId,
                        buyChainId: buyData.chainId,
                        sellValue: sellData.value.toString(),
                        buyValue: buyData.value.toString(),
                        quote: typeof quote === 'string' ? quote : JSON.stringify(quote),
                        referrer: referrer,
                        env: process.env.REACT_APP_ENV,
                    },
                })

                // Update UI with appropriate error message
                updateHistory(exchangeId, {
                    currentStep: failedStep,
                    result: {
                        status: ResultCode.failure,
                        message: errorMessage,
                    },
                })

                // Notify team about the error
                setWaitingForConnectedWalletInteraction(false)
                notifySlackOfError(`Exchange error: ${error}`, {
                    sellData: JSON.stringify(sellData, null, 2),
                    buyData: JSON.stringify(buyData, null, 2),
                    currentStep,
                    walletAddress,
                    destinationAddress,
                    liquiditySource,
                    sellToken: sellData.token?.symbol,
                    buyToken: buyData.token?.symbol,
                    sellChainId: sellData.chainId,
                    buyChainId: buyData.chainId,
                    sellValue: sellData.value.toString(),
                    buyValue: buyData.value.toString(),
                    quote: typeof quote === 'string' ? quote : JSON.stringify(quote),
                    referrer: referrer,
                    env: process.env.REACT_APP_ENV,
                })
            }
        } catch (error) {
            // Top-level error handling
            logger.error('Exchange error:outerTryCatch', error)

            Sentry.captureException(error, {
                tags: {
                    location: 'useExchangeToken:outerTryCatch',
                    exchangeId,
                    liquiditySource,
                },
            })

            updateHistory(exchangeId, {
                currentStep: StepNames.UNSTARTED,
                result: {
                    status: ResultCode.failure,
                    message: 'We encountered an error. Please try again.',
                },
            })
        } finally {
            setWaitingForConnectedWalletInteraction(false)
        }
    }

    return { exchange }
}

export default useExchangeToken
